import React, { createContext, useState, useContext, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';

interface ToastContextType {
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showApiError: (error: any, fallback: string) => void;
}

const ToastContext = createContext<ToastContextType>({} as ToastContextType);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<'success' | 'error'>('success');

    const showSuccess = useCallback((message: string) => {
        setMessage(message);
        setSeverity('success');
        setOpen(true);
    }, []);

    const showError = useCallback((message: string) => {
        setMessage(message);
        setSeverity('error');
        setOpen(true);
    }, []);

    const showApiError = useCallback((error: any, fallback: string) => {
        const apiMessage = error?.response?.data;
        const text = typeof apiMessage === 'string' && apiMessage ? apiMessage : fallback;
        setMessage(text);
        setSeverity('error');
        setOpen(true);
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    return (
        <ToastContext.Provider value={{ showSuccess, showError, showApiError }}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};

export const useToast = () => useContext(ToastContext);
